import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, message } from 'antd';
import { useWriteContract } from 'wagmi';
import { CONTRACT_ADDRESS, CONTRACT_ABI } from '../config/contractConfig'; 
import DashboardLayout from '../components/DashboardLayout';
import '../styles/Generate-and-Accept-InviteCode.css';

const AcceptInvite: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [projectDetails, setProjectDetails] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Extract query params
  const params = new URLSearchParams(location.search);
  const projectId = params.get('projectid');
  const invitationCode = params.get('code');

  const { writeContractAsync, isSuccess, isError, isPending } = useWriteContract(); 

  useEffect(() => {
    if (projectId && invitationCode) {
      // Fetch project details here using the invitation code
      const fetchProjectDetails = async () => {
        try {
          const response = await fetch("https://mobius.hipple.io/api/v1/project/get_projects", {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              token: localStorage.getItem('authToken'),
              chain: 'amoy',
              projects: [projectId],
            }),
          });
          const data = await response.json();

          if (data.error === 0 && data.data.projects.length > 0) {
            const project = data.data.projects[0];
            setProjectDetails({
              title: project.title,
              description: project.content,
              iconUrl: project.icon,
              images: JSON.parse(project.screen),
            });
          } else {
            message.error('Failed to load project details.');
            navigate('/dashboard');
          }
        } catch (error) {
          console.error('Error fetching project details:', error);
          message.error('Error fetching project details.');
        }
      };
      
      fetchProjectDetails();
    } else {
      // Redirect to the dashboard if query params are missing
      message.error('Invalid invite link. Redirecting to dashboard...');
      navigate('/dashboard');
    }
  }, [projectId, invitationCode, navigate]);

  const handleAcceptInvite = async () => {
    if (!invitationCode) {
      message.error('Invalid invitation code.');
      return;
    }

    try {
      setIsLoading(true);

      const tx = await writeContractAsync({
        address: CONTRACT_ADDRESS,
        abi: CONTRACT_ABI,
        functionName: 'acceptInvite',
        args: [invitationCode], 
      });

      if (isPending) {
        console.log('acceptInvite pending:', isPending);
      }

      if (isError) {
        console.log('acceptInvite failed:', isError);
      }

      if (isSuccess) {
        console.log('acceptInvite generated:', isSuccess);
      }

      message.success('Invitation successfully accepted!');
      navigate('/dashboard'); // Redirect to the dashboard or appropriate page
    } catch (error: any) {
      console.error('Error accepting invite:', error);
      message.error('Failed to accept the invitation. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="accept-invite-container">
      {/* Upper Half */}
      <div className="accept-invite-upper">
        <img
          src={projectDetails?.iconUrl || require('../assets/images/icon.png')}
          alt="Project Icon"
          className="accept-invite-icon"
        />
        <h1 className="accept-invite-title">{projectDetails?.title || 'Loading...'}</h1>
        {/* <p className="accept-invite-subtitle">STARFISH TECHNOLOGY - FZE</p> */}
        <Button
          type="primary"
          onClick={handleAcceptInvite}
          loading={isLoading}
          className="accept-invite-button"
        >
          Accept Invite
        </Button>
      </div>

      {/* Lower Half */}
      <div className="accept-invite-lower">
        {projectDetails?.images && projectDetails.images.map((img: string, index: number) => (
          <img
            key={index}
            src={img}
            alt={`Screenshot ${index + 1}`}
            className="accept-invite-screenshot"
          />
        ))}
      </div>

      {/* Text Section */}
      <div className="accept-invite-text">
        <h2>{projectDetails?.title || 'Loading...'}</h2>
        <p>{projectDetails?.description || 'Loading project description...'}</p>
      </div>
    </div>
  );
};

export default AcceptInvite;
